import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { MagnifyingGlass, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CourtListenerSearch({ caseId, onImported, onSelect }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [importing, setImporting] = useState(null);
    const [fetchingId, setFetchingId] = useState(null);

    const selectMode = !caseId && typeof onSelect === "function";

    const search = async (e) => {
        e?.preventDefault();
        if (!q.trim()) return;
        setSearching(true);
        try {
            const { data } = await api.get(`/court/search`, { params: { q, page_size: 10 } });
            setResults(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSearching(false); }
    };

    const importDocket = async (docketId) => {
        setImporting(docketId);
        try {
            await api.post("/court/import", { case_id: caseId, docket_id: docketId });
            toast.success("Imported into Court Record");
            onImported?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setImporting(null); }
    };

    const fetchRecap = async (docketId) => {
        setFetchingId(docketId);
        try {
            const { data } = await api.post("/court/recap-fetch", { case_id: caseId, docket_id: docketId, max_docs: 3 });
            if (data.fetched === 0) {
                toast.warning(data.note || "No free RECAP PDFs on this docket.");
            } else {
                toast.success(`Pulled ${data.fetched} document${data.fetched > 1 ? "s" : ""} and ran OCR.`);
                onImported?.();
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setFetchingId(null); }
    };

    return (
        <Card className="p-5 space-y-3" data-testid="courtlistener-search">
            <div className="flex items-center gap-2">
                <MagnifyingGlass size={18} weight="duotone" className="text-[#2563EB]" />
                <h3 className="font-semibold">Search CourtListener (RECAP federal dockets)</h3>
            </div>
            <p className="text-xs text-slate-500">
                {selectMode
                    ? "Find the docket on CourtListener and pre-fill this form. After saving you can pull the full court record + RECAP PDFs from the Court Record tab."
                    : "Find your case in the federal RECAP archive. Click \"Import\" to populate the Court Record below."}
            </p>
            <form onSubmit={search} className="flex gap-2">
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="e.g. 'Discover Bank Garcia' or '1:25-cv-01845'"
                    className="h-10"
                    data-testid="cl-query"
                />
                <Button type="submit" disabled={searching} data-testid="cl-search-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-10">
                    {searching ? "Searching…" : "Search"}
                </Button>
            </form>

            {results && (
                <div className="space-y-2" data-testid="cl-results">
                    <p className="text-xs text-slate-500">
                        {results.count.toLocaleString()} match{results.count !== 1 ? "es" : ""} — showing top {results.results.length}.
                    </p>
                    {results.results.map((r) => (
                        <div key={r.docket_id} className="border border-slate-200 rounded-md p-3 hover:bg-slate-50 flex items-start gap-3" data-testid={`cl-result-${r.docket_id}`}>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{r.case_name}</p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {[r.court_citation, r.docket_number, r.date_filed].filter(Boolean).join(" · ")}
                                </p>
                                {r.nature_of_suit && (
                                    <p className="text-xs text-slate-500 mt-1">{r.nature_of_suit}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                                {r.absolute_url && (
                                    <a
                                        href={r.absolute_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Open the full docket on CourtListener"
                                        data-testid={`cl-view-docket-${r.docket_id}`}
                                    >
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 text-[#0F1E3D] border-[#0F1E3D]/30 hover:bg-[#0F1E3D]/5"
                                        >
                                            <ArrowSquareOut size={12} className="mr-1" />
                                            View Docket
                                        </Button>
                                    </a>
                                )}
                                {selectMode ? (
                                    <Button
                                        size="sm"
                                        onClick={() => onSelect(r)}
                                        data-testid={`cl-select-${r.docket_id}`}
                                        className="bg-[#2563EB] hover:bg-[#1D4ED8] h-8"
                                    >
                                        <DownloadSimple size={12} className="mr-1" />
                                        Import
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            size="sm"
                                            onClick={() => importDocket(r.docket_id)}
                                            disabled={importing === r.docket_id}
                                            data-testid={`cl-import-${r.docket_id}`}
                                            className="bg-[#2563EB] hover:bg-[#1D4ED8] h-8"
                                        >
                                            <DownloadSimple size={12} className="mr-1" />
                                            {importing === r.docket_id ? "Importing…" : "Import"}
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => fetchRecap(r.docket_id)}
                                            disabled={fetchingId === r.docket_id}
                                            data-testid={`cl-recap-${r.docket_id}`}
                                            variant="outline"
                                            className="h-8 text-[#0F1E3D] border-[#0F1E3D]/30 hover:bg-[#0F1E3D]/5"
                                            title="Auto-fetch free RECAP PDFs + run Gemini OCR"
                                        >
                                            {fetchingId === r.docket_id ? "Fetching…" : "+ Docs"}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}
